/**
 * The anatomy map's browser chrome: metric chips, legend, detail panel, and the
 * three notices that keep the grid honest about what it could not determine.
 *
 * **The grid itself lives in `../render/card-map.ts`.** It was extracted there
 * so a second consumer — the OpenGraph card Lambda of plan §23 — renders the
 * *same* layout from the *same* metadata rather than a lookalike. A share card
 * is only trustworthy by construction if there is one renderer; two would drift
 * within a milestone, and the drift would be invisible until someone compared a
 * card against the app it claims to depict.
 *
 * What stays here is everything that is meaningless without a browser: HTML
 * chrome, the hover/focus detail panel, and the metric chips. The interactive
 * SVG's hover targets, `data-` attributes and ids come from the shared module's
 * `interactive` mode; `card` mode emits none of them.
 *
 * Colour reaches the shared module as `CSS_VAR_PALETTE` — still custom-property
 * references, so the map re-themes with the page exactly as before, including
 * the deliberately inverted dark-mode ramp. See `render/palette.ts` for why the
 * card consumer cannot do the same.
 */

import {
  type AnatomyMarginals,
  computeMarginals,
} from "../analysis/anatomy-marginals.js";
import { type Anatomy, type AnatomyCell, notAWholeModel } from "../analysis/anatomy.js";
import { DRAFT_LABEL } from "../analysis/towers.js";
import {
  type MapBand,
  type MapBandSize,
  type StatSegment,
  anatomyStats,
  anatomyTotals,
  comparesByDifference,
  distinctDtypes,
  escapeXml as escapeHtml,
  interactiveMapLayout,
  legendModel,
  metricLabel,
  renderMapSvg,
} from "../render/card-map.js";
import { CSS_VAR_PALETTE } from "../render/palette.js";
import { cardTitle } from "./card.js";
import { explainForDtype } from "./dtype-explanations.js";
import {
  EXPLANATIONS,
  type ExplainKey,
  explainIcon,
  schemeCostSentence,
} from "./explain.js";
import { formatBytes, formatParams } from "./format.js";
import { setRovingTabindex } from "./roving-focus.js";

// Re-exported rather than moved-and-rewired: these are the map's public surface
// as far as `main.ts` is concerned, and their definitions had to travel to the
// shared module because the card renderer needs them too.
import type { AnatomyMetric } from "../render/card-map.js";
export { defaultMetric, type AnatomyMetric } from "../render/card-map.js";

/**
 * Which stack the grid is of, and how much of the model that leaves out.
 *
 * Present only when the model has more than one tower. The grid is built from
 * one tower's tensors because a layer index means nothing across stacks (see
 * `cellKey` in `analysis/anatomy.ts`) — and a partial view that does not say it
 * is partial is the same lie in a different place, so the counts travel with it.
 */
export interface TowerScope {
  /** The tower's own label — `text`, `vision`, `shared`. */
  readonly label: string;
  readonly tensors: number;
  readonly totalTensors: number;
  readonly bytes: number;
  readonly totalBytes: number;
  /**
   * The whole checkpoint's folded parameter count, so the tower's figure can
   * say what it is a share *of*. Undefined before the first load completes.
   */
  readonly totalParams: number | undefined;
  /** Every tower in detector order, with the mapped one marked. */
  readonly all: readonly {
    readonly id: string;
    readonly label: string;
    readonly selected: boolean;
  }[];
}

/** The second repo, when compare mode is open. */
export interface CompareScope {
  /**
   * B's grid, restricted to the same tower as A's. `undefined` when the two
   * models' towers cannot be lined up — see `declined`, which then says why.
   */
  readonly anatomy: Anatomy | undefined;
  readonly aLabel: string;
  readonly bLabel: string;
  /** Why B contributes no per-cell values, when it does not. */
  readonly declined: string | undefined;
  /**
   * B's own tower scope and part, so the header can state both sides.
   *
   * The header used to describe A alone while the grid drew both — one
   * `Part: UD-IQ1_S` with no hint whose, and one statistics line that a reader
   * comparing two quantizations would take for the pair (owner, 2026-08-12).
   * Neither is a number to *derive*: they are B's own, read from B's inventory.
   */
  readonly scope: TowerScope | undefined;
  readonly part: PartLine | undefined;
  /**
   * Set when B's stack was chosen by **measurement** rather than named by A —
   * the extraction case, where the two models spell their stacks differently
   * and only the shapes can say which is which.
   *
   * A grid that is "A against B's text tower" has to say so rather than imply B
   * entire: the same rule as OG-SPEC §12.4's "both are the selected tower,
   * which is permitted provided the tower is named".
   *
   * The counts come with it because a fraction is what makes thin evidence look
   * thin. "385 of 385" and "19 of 19" are both a clean sweep and are not worth
   * the same, and stating the number lets a reader see that without the page
   * choosing a threshold on their behalf.
   */
  readonly matched:
    | {
        /** The stack that was identified. */
        readonly tower: string;
        /**
         * **Whose** stack it is, and the field exists because the sentence was
         * wrong in one of the two directions without it.
         *
         * The scorer is symmetric — it takes the single-stack side and the
         * multi-stack side, never A and B — so either model can be the one with
         * stacks. The prose was not: it said "B's text stack" and "A's cells"
         * unconditionally, which is right when the extraction is A and exactly
         * backwards when the visitor loads the parent first. Caught on screen,
         * because both readings render.
         */
        readonly onSide: "a" | "b";
        readonly shared: number;
        readonly of: number;
      }
    | undefined;
}

/**
 * Which model inside a repo, and whether there is another to switch to.
 *
 * No URL. Changing a part is done in place — by the loader bar's control, or by
 * clicking this line, both of which open the same modal — because navigating to
 * a part discards a comparison before the new part has even been chosen.
 */
export interface PartLine {
  readonly label: string;
  /** False when the repo holds exactly one model: a choice of one is not one. */
  readonly switchable?: boolean;
}

export interface AnatomyMapModel {
  readonly anatomy: Anatomy;
  readonly metric: AnatomyMetric;
  /**
   * Whether to render the Report issue button (REPORT-SPEC §5.1).
   *
   * A field rather than an import of the flag, so this renderer stays a pure
   * function of its model — the same discipline `card-map.ts` follows for
   * colours. It also keeps the button out of the **shared** renderer entirely:
   * §5.1 requires it never to appear in the SVG map or the OG card, and a
   * module-level flag read here could not distinguish the two consumers.
   */
  readonly reportEnabled?: boolean;
  /** The repo this map describes, e.g. `moonshotai/Kimi-K3`. */
  readonly repoId?: string;
  /** Set when the model has towers and the grid is one of them. */
  readonly scope?: TowerScope;
  /** Set in compare mode; makes every cell carry A's value and B's. */
  readonly compare?: CompareScope;
  /**
   * Which part of the repo this map is of, when the repo holds several.
   *
   * The map's numbers are a property of the part, not only of the model: on
   * `unsloth/Qwen3.6-35B-A3B-GGUF` the same repo offers variants at 4.5 and 8.5
   * bits per weight, and nothing else on screen says which one is drawn. A
   * screenshot of this map was unattributable (owner, 2026-08-09).
   *
   * Applies to safetensors components too — `vae/`, `unet/` — for the same
   * reason, which is why it is not a GGUF-specific field.
   */
  readonly part?: PartLine;
  /**
   * A disclosure about the file's own format, shown beside the placement one.
   *
   * Set for a GGUF file carrying types with no published definition. The two
   * are deliberately separate: `unclassified` is a gap in *our* rule table and
   * says nothing about the model, while this is a fact about the file — the
   * publisher shipped a format they have not documented. Merging them would let
   * a reader conclude the tool is confused when the file is simply private.
   */
  readonly formatNotice?: string | undefined;
  /**
   * Set when the model carries EXL3 trellis modules.
   *
   * Its own field rather than a second use of `formatNotice`, and the reason is
   * the same one that keeps `formatNotice` apart from `unclassified`: they are
   * different claims. `formatNotice` says the publisher shipped a format they
   * have not documented. This says the format is known and documented and *we*
   * have not verified our reading of it — a limitation of ours, stated as ours,
   * which is the distinction `explainUndecodable` exists to preserve.
   */
  readonly exl3Notice?: string | undefined;
}

/**
 * The repo this map is of, linked back to the Hub.
 *
 * Two jobs. It names the model on the card itself, so a screenshot of the map
 * says what it is a map of — the repo id is otherwise only in the field at the
 * top of the page and in the URL. And it is the way back: someone who arrived on
 * a shared deep link has no other route from here to the model's own page.
 *
 * No connecting word. The size and colour difference already says "heading,
 * then subject", so "of" was a word doing work the type had done — and it read
 * as a sentence where two things sitting side by side read as a title and its
 * subtitle.
 *
 * Hidden below the desktop breakpoint by `.anatomy-subject`, where the title has
 * to share a narrow line with its status note and a long id would wrap the
 * heading into three lines. The repo id is still on screen in the field above,
 * so nothing is lost — only repeated less.
 *
 * `encodeURI` on the href and escaping on the text, because a repo id is a
 * string that came off the network.
 */
function anatomySubject(repoId: string | undefined): string {
  if (!repoId) return "";
  const href = `https://huggingface.co/${encodeURI(repoId)}`;
  return (
    `<span class="anatomy-subject"><a href="${escapeHtml(href)}" target="_blank"` +
    ` rel="noopener noreferrer" class="mono">${escapeHtml(repoId)}</a></span>`
  );
}

/**
 * The part line, under the heading.
 *
 * "Switch part" is a link to this repo *without* `?part=`, not a control with
 * its own state. That URL already means "which part was meant?" and lands on
 * the existing picker, so the affordance needs no new mode, stays shareable,
 * and the back button returns to the map. It is omitted when there is nothing
 * to switch to.
 */
function onePart(part: PartLine, side: string | undefined): string {
  const lead = side ? `${side.toUpperCase()} part:` : "Part:";
  const name = `<span class="mono">${escapeHtml(part.label)}</span>`;
  if (!part.switchable) {
    return `<span class="anatomy-part-one">${escapeHtml(lead)} ${name}</span>`;
  }
  /*
   * The value itself is the control, opening the same modal the loader bar's
   * part box does (owner, 2026-08-12) — a reader looking at "A part: Q4_K_M"
   * above the map is already thinking about that part, and making them travel
   * back to the bar to change it is a trip for nothing.
   *
   * A button, not a link. The link it replaced navigated, and navigating to a
   * part is what used to end a comparison.
   */
  return `<span class="anatomy-part-one">${escapeHtml(lead)} <button type="button"
    class="anatomy-part-switch" data-part-open="${escapeHtml(side ?? "a")}"
    aria-haspopup="dialog"
    aria-label="${escapeHtml(
      side
        ? `Change model ${side.toUpperCase()}'s part`
        : "Change which model inside this repo",
    )}">${name}</button></span>`;
}

/**
 * The part line(s), under the heading.
 *
 * In compare mode **both sides get one**, each naming which it is and each with
 * its own switch. A single unlabelled `Part: UD-IQ1_S` above a grid drawing two
 * models is a fact with no subject — and worse when the two happen to share a
 * repo, where it reads as the pair's part rather than A's.
 *
 * **Neither carries a switch any more** (owner, 2026-08-12). Switching used to
 * be a link to the same URL without that side's part — no new mode, shareable,
 * back button returns. What it also was, unavoidably, is a *document reload*:
 * it discarded the comparison before the new part had even been chosen, and
 * returned a solo map of a model that had been half of a pair a moment earlier.
 * The loader bar's part control does it in place instead, so this line is a
 * statement of what is on screen and the control beside the model id is where
 * it is changed.
 */
function renderPart(
  part: AnatomyMapModel["part"],
  compare: CompareScope | undefined,
): string {
  const bPart = compare?.part;
  if (!part && !bPart) return "";
  const paired = compare !== undefined && (part !== undefined || bPart !== undefined);
  const pieces = [
    part ? onePart(part, paired ? "a" : undefined) : "",
    bPart ? onePart(bPart, "b") : "",
  ].filter(Boolean);
  return `<p class="anatomy-part">${pieces.join(
    ' <span aria-hidden="true">·</span> ',
  )}</p>`;
}

export function renderAnatomyMap(model: AnatomyMapModel): string {
  const { anatomy, metric } = model;
  if (anatomy.roles.length === 0) {
    return `<div class="card"><p class="prose prose--muted">No tensors to map.</p></div>`;
  }

  const legend = renderLegend(anatomy, metric, model.compare);

  // Always offered, in both modes: see `defaultMetric`.
  const chips = `<div class="chips" role="group" aria-label="Colour the map by">
        ${(["dtype", "bpw", "bytes", "params"] as const)
          .map(
            (m) =>
              `<button class="chip" type="button" data-metric="${m}" aria-pressed="${m === metric}">${escapeHtml(
                metricLabel(m, anatomy.mode),
              )}</button>`,
          )
          .join("")}
      </div>`;

  /*
   * The action row (REPORT-SPEC §5.1): the four metric chips, then Report issue.
   *
   * DOM and keyboard order is metrics-then-report, and `margin-inline-start:
   * auto` on the button pushes it to the right edge on a wide card. Ordering by
   * CSS instead would put the button before the metrics for a keyboard user
   * while it looked last — the classic disagreement between visual and focus
   * order, and this row is exactly where it would bite: someone tabbing to the
   * metric they want would meet a "report" button first.
   *
   * Rendered only when `reportEnabled` is set, which is the build-time flag —
   * so with the feature off there is no markup, not merely a hidden control.
   */
  /*
   * Share sits before Report, and the order is the one thing worth deciding.
   *
   * Sharing is the common, positive action; reporting is the rare one and the
   * one nobody reaches for by accident. Putting Share last would also place it
   * after a button whose label reads as a warning, which is a strange way to
   * end a row about a model you liked enough to look at.
   *
   * Both come after the metric chips, for the reason recorded above: someone
   * tabbing to the metric they want should not meet either button first.
   */
  /*
   * The share glyph — a box with an arrow leaving it — rather than the word.
   *
   * It is the one icon in this family that is genuinely universal: iOS, macOS
   * and Android all use this exact shape for "send this somewhere else", so it
   * reads without a label in a way that, say, a bare gear or a bare arrow does
   * not.
   *
   * **An icon-only control has no accessible name**, which is the whole cost of
   * this change and the reason both of the following are here rather than one:
   *
   * - `aria-label` is what a screen reader and a voice-control user address it
   *   by. Without it the button announces as "button" and cannot be spoken to.
   * - `title` is what a sighted visitor gets on hover, which is the only way
   *   they can check a guess about a glyph they did not recognise.
   *
   * The two carry the same words on purpose. A voice user saying what they see
   * in the tooltip has to hit the name the control actually answers to.
   *
   * `aria-hidden` on the `<svg>` and `focusable="false"` for the same reason
   * the logo carries them: the graphic is not a second thing to announce or a
   * second stop in the tab order, and IE-era SVG defaults still make the latter
   * worth stating.
   */
  const shareGlyph = `<svg class="share-glyph" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M12 3.5v10.5M12 3.5 8.5 7M12 3.5 15.5 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.5 11H5.5A1.5 1.5 0 0 0 4 12.5v6A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-6A1.5 1.5 0 0 0 18.5 11h-1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  const share = `<button class="glyph-button share-view" type="button" data-share-open aria-label="Share this view" title="Share this view">${shareGlyph}</button>`;
  const actions = model.reportEnabled
    ? `<div class="anatomy-actions">${chips}${share}<button class="report-issue" type="button" data-report-open>Report issue</button></div>`
    : `<div class="anatomy-actions">${chips}${share}</div>`;

  return `<div class="card anatomy-card">
    ${cardTitle(
      // The heading and the repo id share one wrapper so they share one inline
      // formatting context — which is what makes their baselines line up
      // despite the size difference. As separate children of the flex
      // `.card-title` they were two independent items, centred against each
      // other, so the smaller text floated mid-cap-height instead of sitting on
      // the same line as the words beside it.
      `<span class="anatomy-heading">Model anatomy${anatomySubject(model.repoId)}</span><span class="card-title-note">metadata analysis · no weights fetched</span>`,
    )}
    ${renderPart(model.part, model.compare)}
    ${renderTowerStats(model.scope, anatomy, model.compare ? "a" : undefined)}
    ${
      model.compare?.anatomy
        ? renderTowerStats(model.compare.scope, model.compare.anatomy, "b")
        : ""
    }
    ${actions}
    ${renderCompareNote(anatomy, metric, model.compare)}
    ${legend}
    ${renderBands(anatomy, metric, model.compare?.anatomy)}
    ${renderScopeNote(model.scope)}
    <div class="anatomy-tip" id="anatomy-tip" role="status" aria-live="polite"></div>
    ${renderAmbiguity(anatomy)}
    ${renderCrossCheck(anatomy)}
    ${renderBudget(anatomy)}
    ${renderExl3(model.exl3Notice)}
    ${renderBuffers(anatomy)}
    ${renderRules(anatomy, model.formatNotice)}
  </div>`;
}

/* -- layout ---------------------------------------------------------------- */

function band(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  which: MapBand,
  size: MapBandSize,
  compare: Anatomy | undefined,
  marginals: AnatomyMarginals | undefined,
) {
  return renderMapSvg({
    anatomy,
    metric,
    width: size.width,
    height: size.height,
    palette: CSS_VAR_PALETTE,
    mode: "interactive",
    band: which,
    compare,
    marginals,
  });
}

/**
 * Three boxes: pinned row labels, the scrolling stack, the pinned non-layer
 * column.
 *
 * **Only the unbounded axis scrolls.** Layer counts run to the hundreds, so the
 * grid has to scroll somewhere; role counts and the non-layer column do not, so
 * neither has any business being inside the scroller. When all three were one
 * SVG in one scroller, a 24-layer model already pushed the non-layer column past
 * the card's right edge — and that column is where the *embedding* lives, so the
 * `embed` row rendered as a row label with nothing beside it on a model whose
 * embedding is a quarter of its bytes. Scrolling to find it then took the row
 * labels away with it.
 *
 * The two edges are marked whether or not JavaScript runs: a rule between each
 * pinned band and the scroller is CSS, and the fades that say *which* direction
 * has more are toggled by `main.ts` on scroll. A scroller whose only affordance
 * is a macOS overlay scrollbar is a scroller nobody knows is there.
 */
function renderBands(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  compare: Anatomy | undefined,
): string {
  /*
   * Marginals are a **page** feature. `renderMapSvg` is shared with the
   * og-image Lambda, and the share card is a published artifact governed by
   * OG-SPEC — so the option exists, the page passes it, and the card does not
   * (owner's ruling, 2026-08-14). `card-map.test.ts`'s golden is what keeps
   * that true rather than a comment.
   *
   * Not computed in compare mode: with two models in every cell, one row total
   * would have to be A's or B's or a blend, and a number that does not say
   * which is the confidently-ambiguous kind this project refuses. Compare-mode
   * marginals are their own design question.
   */
  const marginals = compare ? undefined : computeMarginals(anatomy);
  const layout = interactiveMapLayout(anatomy, marginals !== undefined);
  const globalBand = layout.global
    ? `<div class="anatomy-pinned anatomy-pinned--global">${band(anatomy, metric, "global", layout.global, compare, marginals)}</div>`
    : "";
  // The grid role belongs to the wrapper, not to a band: three `<svg>` elements
  // are one grid, and `enhanceAnatomyMap` below reads exactly this contract.
  return `<div class="anatomy-map" role="grid" aria-label="Model anatomy: roles by layer">
      <div class="anatomy-pinned anatomy-pinned--labels">${band(anatomy, metric, "labels", layout.labels, compare, marginals)}</div>
      <div class="anatomy-scroll-wrap">
        <div class="anatomy-scroll">${band(anatomy, metric, "layers", layout.layers, compare, marginals)}</div>
        <span class="anatomy-edge anatomy-edge--start" aria-hidden="true"></span>
        <span class="anatomy-edge anatomy-edge--end" aria-hidden="true"></span>
      </div>
      ${globalBand}
    </div>`;
}

/* -- legend ---------------------------------------------------------------- */

/**
 * The swatches, or the sentence that replaces them.
 *
 * `legendModel` decides which; this only draws it. A degenerate domain gets no
 * swatch at all — a ramp with one value on it encodes nothing, and printing its
 * endpoints beside two identical numbers put three colours on screen that
 * appeared nowhere in the grid they claimed to explain.
 */
function renderLegend(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  compare: CompareScope | undefined,
): string {
  const model = legendModel(anatomy, metric, CSS_VAR_PALETTE, compare?.anatomy);
  const keys = model.keys
    .map(
      ({ color, label }) =>
        `<span class="key"><span class="swatch" style="background:${color}"></span>${escapeHtml(label)}</span>`,
    )
    .join("");
  /*
   * `legendModel` states the subject itself when the diverging scale is in
   * force ("shading is A minus B"), so this only has to cover the case where it
   * cannot: `dtype` is categorical, has no difference to shade, and keeps A's
   * colouring while every cell still prints B's value beside it — a scale with
   * two candidate subjects unless one is named.
   */
  const whose = compare?.anatomy && metric === "dtype" ? " · shading is A" : "";
  // The explain icon rides the legend note rather than the metric chips: the
  // note is the one place that names what the colours currently *mean*, and a
  // chip cannot carry it — a button may not contain another button.
  return `<div class="legend">${keys}<span class="legend-note">${escapeHtml(
    model.note + whose,
  )}${explainIcon(METRIC_EXPLAIN[metric])}</span></div>`;
}

/**
 * `1,650 tensors (of 1,986)` — the page's own first statistic.
 *
 * Composed here rather than inside `anatomyStatsLine` for two reasons, and the
 * second is the load-bearing one.
 *
 * It is **page-only**: a share card has three statistics by §12.5 and a tensor
 * count is not among them, so putting it in the shared function would mean a
 * flag whose only job is to switch it off for the other caller.
 *
 * And it is **physical, not logical** — the count of rows in the file, matching
 * the list beside the map. `anatomyStatsLine` reads the anatomy, whose cells
 * are *folded*: this tower holds 1,650 physical tensors in 850 logical ones,
 * because a packed projection is a payload plus a scale plus a shape. Both
 * counts are honest and they answer different questions; deriving this one from
 * the anatomy would silently switch which question the line answers.
 */
function towerTensorCount(scope: TowerScope): StatSegment {
  return {
    value: `${scope.tensors.toLocaleString()} tensors`,
    of:
      scope.totalTensors > scope.tensors
        ? `(of ${scope.totalTensors.toLocaleString()})`
        : undefined,
  };
}

/**
 * One statistic as markup: the measurement in full ink, its total muted.
 *
 * The `(of …)` is the context, not the reading. Given equal weight the line is
 * eight numbers of identical importance and the eye has nowhere to land; muted,
 * the four measurements come forward and the totals stay available to anyone
 * who wants to know what they are a share of (owner, 2026-08-10).
 *
 * `lead` marks bits-per-weight, which is the one figure this whole tool exists
 * to report and the only one whose definition nobody gets to choose.
 */
function renderStat(stat: StatSegment): string {
  const value = stat.lead
    ? `<strong>${escapeHtml(stat.value)}</strong>`
    : escapeHtml(stat.value);
  const of = stat.of
    ? ` <span class="tower-stats-of">${escapeHtml(stat.of)}</span>`
    : "";
  return `${value}${of}`;
}

/**
 * The share card's two header rows, on the page.
 *
 * ```
 *     Towers available: text · vision · shared
 *                       ↳ 1,650 tensors (of 1,986) · 25.6B params (of 27.4B) · …
 * ```
 *
 * Deliberately the *same* two rows the card shows, in the same order, from the
 * same `anatomyStatsLine` the card calls — so someone arriving from a shared
 * link reads the figures they just saw rather than having to match them up
 * (owner, 2026-08-10). A second implementation here would be one edit away from
 * the page and the card disagreeing about a model.
 *
 * **Both rows are the selected tower.** OG-SPEC §12.4 permits tower figures
 * provided the tower is named; the row above is that naming, and the `(of …)`
 * on each figure is what makes the scope explicit *in the numbers* rather than
 * in prose beside them.
 *
 * The page carries more than the card does — a tensor count, and the totals —
 * because it has the width and because a visitor who followed the link has
 * earned more than the preview. The card stays at §12.5's three statistics.
 *
 * ## Why the label is its own element
 *
 * The card runs "Towers: " and the stack names on one baseline, then hangs the
 * statistics off an elbow indented by the label's width — arithmetic it can do
 * because it measures its own text. On the page the label sits in one `<div>`
 * and *both* rows in a second, so the statistics line up with the **first tower
 * name** rather than with a guessed indent (owner, 2026-08-10). Flexbox does
 * the measuring, so the alignment holds whatever the label says and at any font
 * size.
 *
 * Omitted entirely for a single-tower model, like the card's own tower line:
 * "Towers available: text" is a list of one, and the statistics would then
 * describe the whole model while implying a choice that does not exist.
 */
function renderTowerStats(
  scope: TowerScope | undefined,
  anatomy: Anatomy,
  side?: string,
): string {
  if (!scope) return "";
  const stats = [
    towerTensorCount(scope),
    ...anatomyStats(anatomy, { params: scope.totalParams, bytes: scope.totalBytes }),
  ]
    .map(renderStat)
    .join(' <span aria-hidden="true">·</span> ');

  /*
   * The side marker, in compare mode only.
   *
   * Without it the header stated one set of figures over a grid drawing two,
   * and a reader comparing two quantizations of one repo had no way to tell
   * whether "2.37 bits/weight" was A's, B's or some combination (owner,
   * 2026-08-12). It is not a derived number and cannot be: each side has its
   * own tensors, parameters and bytes.
   */
  const marker = side
    ? `<span class="tower-stats-side" aria-hidden="true">${escapeHtml(side.toUpperCase())}</span>`
    : "";
  const named = side
    ? `<span class="visually-hidden">${escapeHtml(side.toUpperCase())}: </span>`
    : "";

  /*
   * A model with one tower gets the statistics and no tower row.
   *
   * "Towers available: text" is a list of one — it offers a choice that does
   * not exist, and the `(of …)` totals vanish on their own because the tower
   * *is* the checkpoint. The statistics themselves are not tower-dependent and
   * never were: `gpt2` has 160 tensors, 137M parameters and a size, and used to
   * show none of them because one `undefined` decided three questions at once.
   */
  /*
   * "This is not a whole model", when the structure says so.
   *
   * Sits with the statistics rather than beside the map, because it is the same
   * kind of statement the rows around it make: what the figures are *of*. A
   * visitor who opened a 186 MB repo called `…-27B-MTP-…` has one question, and
   * `425M params` answers it only if something says that is the answer.
   *
   * Structure only — see `notAWholeModel`. It never names the specific kind,
   * because the anatomy cannot tell an MTP head from a draft head or an adapter
   * and the repo's name is not evidence.
   */
  const notWhole = notAWholeModel(anatomy);
  /*
   * **The subject is the stack, not the repo**, and the difference is not
   * pedantry — it was wrong first.
   *
   * `mlx-works/Qwen3.8-27B-oQ2e-mtp` reads `32 tensors (of 2,209) · 1.70B params
   * (of 27.8B)`: a *whole* 27.8B model that happens to carry an MTP stack, with
   * that stack selected. The first wording said "repos shaped like this hold a
   * head that runs beside a full model rather than replacing it", which is false
   * of that repo — the full model is right there, one chip away. Caught by
   * checking three models instead of the one the sentence was written for.
   *
   * So the solo case may talk about the repo, because the stack *is* the repo;
   * the multi-tower case may only talk about the stack, and points at the row
   * above where the others are listed.
   */
  const solo = scope.all.length < 2;
  const aside = notWhole
    ? `<p class="tower-aside">${
        solo
          ? `This has ${escapeHtml(notWhole)} — too little to be a whole model on its
             own. Repos shaped like this hold a ${escapeHtml(DRAFT_LABEL)} or
             prediction head that runs beside a full model rather than replacing it.`
          : `The stack shown has ${escapeHtml(notWhole)} — it is a
             ${escapeHtml(DRAFT_LABEL)} or prediction head rather than a model in its
             own right. The model's other stacks are listed above.`
      }</p>`
    : "";

  if (scope.all.length < 2) {
    return `<div class="tower-block tower-block--solo">
      <div class="tower-stats">${marker}${named}<span>${stats}</span></div>
      ${aside}
    </div>`;
  }

  /*
   * The unselected names are buttons, the selected one is not.
   *
   * A visitor reading "text · vision · shared" has already been told a choice
   * exists; making the other two inert asks them to find the chip row to act on
   * it. The selected one stays plain text because a control that does nothing
   * when pressed is worse than no control — its state is the page's state.
   *
   * `data-tower-switch` rather than `data-tower`: the chip row's delegated
   * handler matches on the latter, and two handlers reacting to one click is
   * the kind of thing that works until someone moves an element.
   */
  const towers = scope.all
    .map((t) =>
      t.selected
        ? `<strong class="tower-line-selected">${escapeHtml(t.label)}</strong>`
        : `<button class="tower-switch" type="button" data-tower-switch="${escapeHtml(
            t.id,
          )}">${escapeHtml(t.label)}</button>`,
    )
    .join(' <span aria-hidden="true">·</span> ');
  return `<div class="tower-block">
      <div class="tower-block-label">${explainIcon("towers")}Towers available:</div>
      <div class="tower-block-body">
        <div class="tower-line">${towers}</div>
        <div class="tower-stats"><span class="tower-stats-elbow" aria-hidden="true">↳</span>
        ${marker}${named}<span>${stats}</span></div>
        ${aside}
      </div>
    </div>`;
}

/**
 * How to use the map, as a caption *under* it.
 *
 * Placement is the whole design. It sat above the grid, where a second-time
 * visitor who wants to get straight into the anatomy has to read past it —
 * and instructions are exactly the copy that stops being wanted once it has
 * been followed. Below the legend it works the way a caption under a figure
 * does: found when looked for, skipped when not (owner, 2026-08-10).
 *
 * Italic and centred for the same reason. It is the one line in this card that
 * *instructs* rather than reports; everything else is a measurement, and the
 * slant is what marks the change of register without a heading or an icon to
 * carry it.
 *
 * **This used to carry the scope numbers too** — "1,650 of 1,986 tensors,
 * 12.2 GiB of 13.7 GiB (89%)" — and a sentence promising that everything below
 * counted the whole model. Both are gone (owner, 2026-08-10). The numbers moved
 * up into the tower block, where they sit beside the tower that owns them and
 * gained params as well; repeating them here was one paragraph and one line
 * saying the same thing, and the *prose* copy is the one a reader has to parse
 * rather than scan.
 *
 * The promise had to go for a stronger reason: it was **not true**. The
 * quantization budget renders below this note and is the selected tower's, so a
 * reader following "everything further down the page still counts the whole
 * model" read 3.46 bits/weight as the model's when the model's is 3.49. A
 * sentence that is right about three cards below it and wrong about the fourth
 * is worse than no sentence, and OG-SPEC §12.4's rule — be explicit about
 * scope, never mix silently — is now met the way the share card meets it, by
 * labelling each figure where it appears.
 *
 * What is left is the half that was never redundant, and it is explanation
 * rather than measurement:
 *
 *  - the two panes are **one selection**, not two filters that can disagree;
 *  - a layer index means nothing across towers, which is *why there is no "all"
 *    chip* — `AppState.tower` has the full argument, and without this sentence
 *    the missing chip reads as an oversight rather than a decision.
 *
 * The clause about the tensor list is wrapped, because the list is not there on
 * a phone (see the 560px block in app.css). A caption describing a pane the
 * reader cannot see is the same failure as naming a card that is not on screen.
 */
function renderScopeNote(scope: TowerScope | undefined): string {
  /*
   * "this tower" only when there is more than one.
   *
   * On a single-stack model the phrase names a distinction the reader has not
   * been shown and cannot act on — there is no tower row above and no chip row
   * beside. The instruction is otherwise identical, because clicking a cell is
   * clicking a cell.
   */
  const subject = (scope?.all.length ?? 0) >= 2 ? "this tower" : "this model";
  /*
   * Two wrapped clauses, not one.
   *
   * The list is not on screen below 560px (see app.css), so naming it there
   * would point at a pane that does not exist. Wrapping only the trailing
   * clause would leave "either" dangling with nothing to choose between, so
   * the conjunction travels with it and the narrow reading is a complete
   * sentence in its own right:
   *
   *   wide    Explore the tensor groups in this model, either by clicking a
   *           cell below or in the list to the left.
   *   narrow  Explore the tensor groups in this model by clicking a cell below.
   */
  return `<p class="anatomy-scope">Explore the tensor groups in ${subject}<span
    class="scope-list-note">, either</span> by clicking a cell below<span
    class="scope-list-note"> or in the list to the left</span>.</p>`;
}

/**
 * Compare mode: what the two numbers in each cell are, and what is not drawn.
 *
 * Position is the only thing telling A from B in a 30px cell — there is no room
 * for a label and no colour to spare, since both values sit on one fill and take
 * its validated ink. So the convention has to be stated once, in words, above
 * the grid that uses it.
 *
 * It also has to say **what the colour now means**, and that sentence changes
 * with the metric: the three numeric metrics shade the difference, `dtype`
 * cannot (there is no such thing as "BF16 minus U8") and keeps A's categorical
 * colouring. A single fixed sentence would be false on one of the two.
 */
function renderCompareNote(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  compare: CompareScope | undefined,
): string {
  if (!compare) return "";
  if (!compare.anatomy) {
    return `<div class="notice notice--info">
      <strong>Only A is mapped.</strong> ${escapeHtml(compare.declined ?? "")}
      The grid, its shading and its legend are ${escapeHtml(compare.aLabel)} alone; the diff below
      still compares both models in full.
    </div>`;
  }

  const onlyInB = countOnlyInB(anatomy, compare.anatomy);
  /*
   * Named, and with the evidence beside it. This sentence is the difference
   * between a reader believing the grid compares two whole models and knowing
   * it compares one model against one stack of another — which is the truth
   * whenever an extraction is compared against the checkpoint it came from.
   *
   * It says "this grid" and not "everything below", which it said first. The
   * inventory diff further down the page compares the two repos **in full**, so
   * the wider claim was false the moment it was written — a component asserting
   * a fact about a component it does not own, which is the same defect as a
   * verdict that gestures at the map's layout (#67). Scoping the diff to the
   * matched stack is real work and is not done by a sentence claiming it is.
   */
  const matched = compare.matched
    ? compare.matched.onSide === "b"
      ? `<strong>B is matched by shape, not by name.</strong> These two spell their tensors
         differently, so B's <strong>${escapeHtml(compare.matched.tower)}</strong> stack was
         identified by measurement: ${compare.matched.shared.toLocaleString()} of
         ${compare.matched.of.toLocaleString()} of A's cells sit inside it, and no other stack of
         B comes close. <strong>This grid</strong> compares A against that stack, not against B
         entire. `
      : `<strong>B is matched by shape, not by name.</strong> These two spell their tensors
         differently, and B has no stacks of its own — it was measured against each of A's and
         belongs to the <strong>${escapeHtml(compare.matched.tower)}</strong> stack shown here:
         ${compare.matched.shared.toLocaleString()} of
         ${compare.matched.of.toLocaleString()} of B's cells sit inside it, and no other stack of
         A comes close. `
    : "";
  /*
   * `dtype` is categorical, so there is no difference to shade and the fill
   * stays A's. Said plainly rather than left to be inferred from a legend that
   * looks different on this one chip.
   */
  /*
   * Asked of the renderer rather than re-derived here. `dtype` is the usual
   * reason the fill is still A's, but not the only one — two models sharing no
   * cell at all have no difference to shade either — and a note that names one
   * cause while the legend reflects another is a page explaining a picture it
   * is not drawing.
   */
  const colour = !comparesByDifference(anatomy, compare.anatomy, metric)
    ? metric === "dtype"
      ? `The colour is <strong>A's</strong> storage type — a difference between two dtypes is not
         a quantity, so this one metric keeps A's own colouring and the comparison is in the
         numbers.`
      : `The colour is <strong>A's</strong> value: these two share no cell, so there is no
         difference to shade.`
    : `The colour is the <strong>difference</strong>, A minus B: neutral where the two agree,
       cool where this cell holds <em>less</em> than B's and warm where it holds more. Cells
       drawn as bare plane exist in A and not in B, so they have no difference to show.`;
  return `<p class="anatomy-compare-key">${matched}Each cell carries two values:
    <strong>A above and to the left</strong> (${escapeHtml(compare.aLabel)}),
    <strong>B below and to the right</strong> (${escapeHtml(compare.bLabel)}) — read the offset as
    the slash in <span class="mono">A/B</span>. ${colour} An em dash means
    that side has no number here. No weights were fetched to draw this and no extra request was
    made: every value comes from headers both models had already given up.${
      onlyInB > 0
        ? ` ${onlyInB.toLocaleString()} cell${onlyInB === 1 ? "" : "s"} exist${
            onlyInB === 1 ? "s" : ""
          } in B and not in A, so ${onlyInB === 1 ? "it is" : "they are"} not drawn — the grid is A's.`
        : ""
    }</p>`;
}

/** Cells B has that A's grid has no square for. Counted so it can be stated. */
function countOnlyInB(anatomy: Anatomy, b: Anatomy): number {
  let count = 0;
  for (const key of b.cells.keys()) if (!anatomy.cells.has(key)) count += 1;
  return count;
}

/** Each map metric is a statistic the glossary already explains. */
const METRIC_EXPLAIN: Record<AnatomyMetric, ExplainKey> = {
  dtype: "dtype",
  bpw: "effective-bpw",
  bytes: "bytes",
  params: "params",
};

/* -- ARIA grid semantics --------------------------------------------------- */

/**
 * Everything about the map that only exists once it is in a document.
 *
 * This is deliberately *not* in `render/card-map.ts`, which emits the grid.
 * That module is shared with the plan §23 og-image Lambda and is owned by the
 * workstream reworking the map's *layout*; roving tabindex is stateful runtime
 * behaviour that cannot be expressed in a string anyway, and the row grouping
 * is applied beside it so the two halves of one ARIA contract stay in one
 * place. Nothing here touches geometry — no coordinate, no size, no transform.
 *
 * Three defects, one pass:
 *
 * 1. **`role="grid"` with no `role="row"` children.** ARIA's required-owned-
 *    elements contract says a grid owns rows and rows own cells. The renderer
 *    emits a flat list of `<rect role="gridcell">`, so the grid owned nothing it
 *    was allowed to own and assistive technology had no row structure to
 *    navigate. Wrapping a role's cells costs one `<g>` and moves nothing on
 *    screen: a group with no transform draws exactly what its children drew.
 * 2. **Every cell was a tab stop** — 76 on gpt2, and roughly 200 on a 28-layer
 *    model. The grid pattern is one tab stop plus arrow keys.
 * 3. **Focus landed on cells scrolled out of view.** The grid is wider than its
 *    container by design (the layer axis is unbounded), so tabbing through it
 *    put focus on invisible cells — WCAG 2.2 AA 2.4.11 Focus Not Obscured.
 *    Focusing a cell now scrolls it into view. That is needed whatever the card
 *    does about clipping, which is the layout workstream's.
 *
 * **What this reads, and nothing else.** The contract with the renderer is four
 * things: an element carrying `role="grid"`, `.anatomy-cell` on every cell,
 * `data-role` naming the cell's row and `data-layer` naming its column. Row and
 * column identity come from those attributes rather than from position, so the
 * navigation model survives the map being split across several `<svg>` bands
 * for pinned labels — a row whose cells live in two bands still navigates as
 * one row, because a row is a `data-role`, not a parent node. Grouping is per
 * parent for the same reason: a `<g>` cannot span two documents, so a split row
 * contributes one group per band and each group is still a valid row of cells.
 *
 * Safe to call on every render: it re-reads the DOM it is given and returns
 * early if that DOM already carries rows.
 */
export function enhanceAnatomyMap(root: ParentNode): void {
  const grid = root.querySelector('[role="grid"]');
  if (!grid) return;

  const cells = [...grid.querySelectorAll<SVGRectElement>(".anatomy-cell")];
  if (cells.length === 0) return;
  // Re-entry guard. `renderView` replaces the pane wholesale, so in practice
  // this always sees a fresh grid; a caller that wires the same one twice would
  // otherwise nest a second layer of rows inside the first.
  if (grid.querySelector('[role="row"]')) return;

  const rows = rowsByRole(cells);
  groupIntoRows(rows);
  markPlumbingPresentational(grid);

  const position = new Map<Element, { readonly row: number; readonly col: number }>();
  rows.forEach(({ cells: row }, rowIndex) => {
    row.forEach((cell, colIndex) =>
      position.set(cell, { row: rowIndex, col: colIndex }),
    );
  });

  // The row labels and the axis ticks are `<text>` — not rows, and therefore
  // not children a grid may own. Their content is already carried by the row
  // groups' names and by each cell's own label, so hiding them from the
  // accessibility tree loses nothing and leaves the grid owning only rows.
  for (const label of grid.querySelectorAll("text")) {
    label.setAttribute("aria-hidden", "true");
  }

  setRovingTabindex(cells, 0);

  grid.addEventListener("focusin", (event) => {
    const cell = cellFrom(event.target);
    if (!cell) return;
    setRovingTabindex(cells, cells.indexOf(cell));
    scrollIntoViewIfPossible(cell);
  });

  grid.addEventListener("keydown", (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    const cell = cellFrom(event.target);
    if (!cell) return;
    const at = position.get(cell);
    if (!at) return;
    const next = neighbour(rows, at, event);
    if (!next) return;
    // Claimed even when the move is a no-op at an edge: an arrow key the grid
    // owns must never fall through to scrolling the page instead.
    event.preventDefault();
    if (next === cell) return;
    setRovingTabindex(cells, cells.indexOf(next));
    next.focus();
  });
}

/**
 * Everything between the grid and its rows is layout, and says so.
 *
 * The map is a wrapper holding three pinned/scrolling bands, each holding an
 * `<svg>`, each holding the rows — and a grid may own only rows. Marking the
 * intermediate elements `presentation` drops them from the accessibility tree
 * while leaving their children in it, so a row three levels down inside scroll
 * plumbing is still owned by the grid. Walked upward from the rows rather than
 * downward from the grid, so it marks exactly the elements that are in the way
 * and nothing the layout workstream adds beside them.
 */
function markPlumbingPresentational(grid: Element): void {
  for (const row of grid.querySelectorAll('[role="row"]')) {
    let node = row.parentElement;
    while (node && node !== grid) {
      if (!node.hasAttribute("role")) node.setAttribute("role", "presentation");
      node = node.parentElement;
    }
  }
}

/**
 * The cell an event happened in, or `null`.
 *
 * Narrowed by selector rather than by `instanceof SVGRectElement`: that
 * interface is one of the many SVG ones jsdom does not implement, so the check
 * would throw a `ReferenceError` in the tests while working fine in a browser —
 * a guard whose only failure mode is in the place that checks it.
 */
function cellFrom(target: EventTarget | null): SVGRectElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<SVGRectElement>(".anatomy-cell");
}

/** One row of the grid: the role that names it, and its cells in draw order. */
interface MapRow {
  readonly role: string;
  readonly cells: readonly SVGRectElement[];
}

/**
 * The navigation model: rows keyed by `data-role`, in first-seen order.
 *
 * By attribute rather than by position, so neither a layout change nor the map
 * being split across bands can silently regroup them.
 */
function rowsByRole(cells: readonly SVGRectElement[]): MapRow[] {
  const byRole = new Map<string, SVGRectElement[]>();
  for (const cell of cells) {
    const role = cell.dataset.role ?? "";
    const existing = byRole.get(role);
    if (existing) existing.push(cell);
    else byRole.set(role, [cell]);
  }
  return [...byRole].map(([role, members]) => ({ role, cells: members }));
}

/**
 * Wrap each row's cells in a `<g role="row">`, in place.
 *
 * One group per parent element rather than one per row: a `<g>` cannot span two
 * `<svg>` documents, so a row whose cells are split across a pinned band and a
 * scrolled one contributes a group to each. Both are valid rows of gridcells,
 * and the keyboard model above still treats them as the single row they are.
 *
 * Inserted where the run's first cell was, and the cells moved into it, so
 * paint order is unchanged.
 */
function groupIntoRows(rows: readonly MapRow[]): void {
  for (const { role, cells } of rows) {
    const runs = new Map<Node, SVGRectElement[]>();
    for (const cell of cells) {
      const parent = cell.parentNode;
      if (!parent) continue;
      const run = runs.get(parent);
      if (run) run.push(cell);
      else runs.set(parent, [cell]);
    }

    for (const [parent, run] of runs) {
      const first = run[0];
      if (!first) continue;
      const group = first.ownerDocument.createElementNS(first.namespaceURI, "g");
      group.setAttribute("role", "row");
      // The row's name. The visible label beside it is a `<text>` this pass
      // hides, so without this the row would be an unnamed container.
      group.setAttribute("aria-label", role);
      parent.insertBefore(group, first);
      for (const cell of run) group.appendChild(cell);
    }
  }
}

/**
 * The cell an arrow key moves to, or `null` for a key this grid doesn't own.
 *
 * Vertical movement follows the *layer*, not the column index: rows are ragged
 * by design — `attn.k` stopping short while `attn.q` runs the full depth is a
 * real architectural finding, not a rendering gap — so stepping down by index
 * would drift sideways across a ragged edge and land under a different layer
 * than the one you were reading. Where the target row has no cell at that layer
 * (the ragged case), fall back to the nearest index it does have.
 */
function neighbour(
  rows: readonly MapRow[],
  at: { readonly row: number; readonly col: number },
  event: KeyboardEvent,
): SVGRectElement | null {
  const row = rows[at.row]?.cells;
  if (!row) return null;
  const wholeGrid = event.ctrlKey || event.metaKey;

  switch (event.key) {
    case "ArrowRight":
      return row[Math.min(at.col + 1, row.length - 1)] ?? null;
    case "ArrowLeft":
      return row[Math.max(at.col - 1, 0)] ?? null;
    case "ArrowDown":
      return sameLayer(
        rows,
        Math.min(at.row + 1, rows.length - 1),
        row[at.col],
        at.col,
      );
    case "ArrowUp":
      return sameLayer(rows, Math.max(at.row - 1, 0), row[at.col], at.col);
    case "Home":
      return wholeGrid ? (rows[0]?.cells[0] ?? null) : (row[0] ?? null);
    case "End": {
      if (!wholeGrid) return row[row.length - 1] ?? null;
      const last = rows[rows.length - 1]?.cells;
      return last?.[last.length - 1] ?? null;
    }
    default:
      return null;
  }
}

function sameLayer(
  rows: readonly MapRow[],
  rowIndex: number,
  from: SVGRectElement | undefined,
  fallbackCol: number,
): SVGRectElement | null {
  const row = rows[rowIndex]?.cells;
  if (!row) return null;
  const layer = from?.dataset.layer;
  const aligned =
    layer === undefined ? undefined : row.find((cell) => cell.dataset.layer === layer);
  return aligned ?? row[Math.min(fallbackCol, row.length - 1)] ?? null;
}

/**
 * Bring a focused cell into view, minimally.
 *
 * `block`/`inline: "nearest"` so it scrolls only as far as it must — a grid
 * that recentred on every arrow key would move the whole page under a reader
 * who asked for one step sideways. Feature-detected because a DOM
 * implementation without a layout engine (jsdom, in the tests) does not
 * implement it, and a missing scroll must not break the keyboard navigation
 * that is the actual fix here.
 */
function scrollIntoViewIfPossible(cell: SVGRectElement): void {
  if (typeof cell.scrollIntoView !== "function") return;
  cell.scrollIntoView({ block: "nearest", inline: "nearest" });
}

/**
 * How many names the tip will name before it stops counting.
 *
 * The stats line above already states the exact tensor count, so this cap costs
 * no information — it only stops an MoE cell holding 200 experts from building a
 * 6 kB string on every mouse move.
 */
const MAX_TIP_NAMES = 8;

/**
 * The longest prefix every name shares, cut at a dot.
 *
 * Cut at a dot specifically: the longest *common* prefix of
 * `…self_attn.k_proj` and `…self_attn.q_proj` ends mid-segment at
 * `self_attn.`+nothing useful, and factoring on a character boundary produces
 * `self_attn.{k,q}_proj` — clever, and unreadable. Segment boundaries keep both
 * halves things a reader recognises.
 */
function commonDotPrefix(names: readonly string[]): string {
  const first = names[0] ?? "";
  let end = 0;
  for (let i = 0; i < first.length; i += 1) {
    if (first[i] !== ".") continue;
    const candidate = first.slice(0, i + 1);
    if (!names.every((name) => name.startsWith(candidate))) break;
    end = i + 1;
  }
  return first.slice(0, end);
}

/**
 * The cell's tensor names as **one line**.
 *
 * They used to be one per line, so the panel was between two and five lines tall
 * depending on which cell the pointer was over — and since the panel sits under
 * the map, every row of it that appeared or vanished shoved the cards below it.
 * Moving the mouse across the grid made the page twitch.
 *
 * Factored rather than merely joined, because a plain comma list of
 * `model.layers.5.input_layernorm, model.layers.5.post_attention_layernorm`
 * spends most of its width restating the part the two have in common — which is
 * also the part the cell's own label already told you. Brace notation is the
 * convention developers already read this way, from shell expansion.
 */
function tipNames(names: readonly string[]): string {
  const shown = names.slice(0, MAX_TIP_NAMES);
  const tail = names.length > shown.length ? ", …" : "";

  if (shown.length <= 1) return escapeHtml((shown[0] ?? "") + tail);

  const prefix = commonDotPrefix(shown);
  if (!prefix) return escapeHtml(shown.join(", ") + tail);

  const stems = shown.map((name) => name.slice(prefix.length)).join(", ");
  return escapeHtml(`${prefix}{${stems}}${tail}`);
}

/**
 * One cell's statistics, without the heading that says where it is.
 *
 * ## Order: what it is, then how much of it there is
 *
 * Identity first — the scheme and the storage dtype — then the measurements in
 * the same order the tower block above uses: tensors, params, bytes, bits per
 * weight. The two lines are the only places on the page that run these four
 * figures together, and reading them in different orders costs a beat every
 * time the eye moves between them (owner, 2026-08-10).
 *
 * **The scheme comes before the dtype**, and that is the deliberate half.
 * `3@128 · I32` reads as "three bits at group 128, stored in int32" — the
 * scheme explaining its container. Reversed, `I32` lands first with nothing to
 * qualify it, which is the exact misreading this codebase has now fixed five
 * times: an I32 payload is not 32-bit weights.
 *
 * ## One bold, on bits per weight
 *
 * Matching the tower line, where the same figure is the only emphasised one.
 * `3@128` is arguably as interesting, but bolding both spends the emphasis: two
 * strong runs in a nine-part line mark nothing. The scheme and dtype get
 * `<code>` instead — they are tokens read as identifiers, where the rest are
 * quantities, and that distinction is worth more than a second weight.
 */
function cellFacts(cell: AnatomyCell): string {
  return [
    cell.quantLabel ? `<code>${escapeHtml(cell.quantLabel)}</code>` : "",
    // Escaped, unlike before. A dtype is a *network-controlled string* — the
    // header parser requires only that it be a string and lets unknown ones
    // through on purpose — and this lands in `tip.innerHTML`. The SVG path had
    // been safe all along because `escapeXml` wraps its aria-label; this one
    // had nothing (found 2026-08-10).
    cell.dtypes.length ? `<code>${escapeHtml(cell.dtypes.join(", "))}</code>` : "",
    `${cell.tensors.length} tensor${cell.tensors.length === 1 ? "" : "s"}`,
    `${formatParams(cell.params)} params`,
    formatBytes(cell.bytes),
    `<strong>${cell.effectiveBpw.toFixed(2)} bpw</strong>`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The hover/focus detail panel's content for one cell.
 *
 * In compare mode it is where the em dash a cell printed gets its explanation:
 * a 30px square can say "no number" and cannot say *why*, so "B has no tensors
 * here" and "B's quantization scheme could not be pinned down" are separated
 * out here, in full, on both sides.
 */

/**
 * Everything a cell's type labels mean, as the body of a card.
 *
 * ## Why this is not in the hover panel any more
 *
 * It was, and it made the panel's height swing from 60px to 284px — so sweeping
 * the mouse along a row moved every card below the map, repeatedly, under the
 * cursor. `.anatomy-tip`'s own `min-height` comment states the invariant that
 * broke: sized "so the panel is exactly as tall empty as it is full, and the
 * first hover moves nothing".
 *
 * Variable-length copy cannot live in a surface that a pointer sweeps across.
 * It belongs where the reader has *asked* for detail and expects the page to
 * grow — the region under the map that a selected cell already fills, beside
 * the tensor card and the analysis (owner, 2026-08-19). Hover keeps the facts
 * line, which is one line and never resized anything.
 *
 * ## What it says
 *
 * **Both axes, because a quantized cell names two things that are not the same
 * kind of thing.** An MLX cell reads `4@64` beside `U32`: the first is the
 * scheme, how the weights are encoded; the second is the container the encoded
 * bits sit in. Explaining only the dtype answers the less interesting half.
 *
 * Every dtype present gets its sentence rather than a capped subset. A folded
 * group is `I32` beside `F16` precisely *because* the two are doing different
 * jobs, so dropping one would hide the distinction the fold exists to show.
 *
 * Flat text rather than explain icons: this renders inside a card that is
 * itself already the explanation, so an icon would be a control that opens onto
 * the paragraph it is sitting in.
 */

/**
 * Which tensor is on which scheme, and how much of the cell each one is.
 *
 * Naming the schemes is not enough on its own. `8@64, 4@64` reads as a
 * half-and-half cell, and on
 * `mlx-community/Qwen3.5-4B-OptiQ-4bit`'s layer 8 the 8-bit `in_proj_qkv` is
 * 20.9 MB against 5.3 MB for the three 4-bit parts — so the average sits near
 * the 8-bit end for a reason a reader cannot see from two labels. Share of
 * bytes is what makes the average legible.
 *
 * Bytes rather than parameters, because bytes are what the cell's own figure is
 * computed from and are safe under packing: a byte is a byte however many
 * weights it holds. See the packing invariant in CLAUDE.md.
 */
function schemeBreakdown(cells: readonly AnatomyCell[]): string {
  const parts = cells.flatMap((c) => c.tensors);
  const total = parts.reduce((n, t) => n + t.bytes, 0);
  if (total <= 0) return "";
  const rows = parts
    .filter((t) => t.quantLabel)
    .slice()
    .sort((a, b) => b.bytes - a.bytes)
    .map(
      (t) =>
        `<li><code>${escapeHtml(shortTensorName(t.name))}</code> — ${escapeHtml(
          t.quantLabel as string,
        )}, ${sharePercent(t.bytes, total)} of the bytes</li>`,
    )
    .join("");
  return rows ? `<ul class="cell-schemes">${rows}</ul>` : "";
}

/**
 * A share, with anything present never printed as `0%`.
 *
 * Layer 8's `in_proj_a` is 46KB against 28MB, which rounds to zero — and `0% of
 * the bytes` reads as *absent*, which is the opposite of what the row is there
 * to say. It is listed precisely because it is a 4-bit part of a cell whose
 * average sits at 7.15; a reader needs to see that it exists and is tiny, not
 * that it is nothing.
 */
function sharePercent(bytes: number, total: number): string {
  const pct = (bytes / total) * 100;
  if (pct > 0 && pct < 0.5) return "<1%";
  return `${pct.toFixed(0)}%`;
}

/** The last two segments, which is what distinguishes parts of one cell. */
function shortTensorName(name: string): string {
  return name.split(".").slice(-2).join(".");
}

export function cellTypeNotes(cells: readonly AnatomyCell[]): string {
  const notes: string[] = [];
  const seen = new Set<string>();

  // Scheme first, matching the order `cellFacts` prints them and the order they
  // read in: what the encoding is, then what it is stored in.
  /*
   * Every scheme in the cell, not the cell's own `quantLabel`.
   *
   * A folded cell can hold tensors quantized differently, and `anatomy.ts` keeps
   * a single label for it — `tensor.quantLabel ?? existing?.quantLabel`, so
   * whichever part merged first wins. On `mlx-community/Qwen3.5-4B-OptiQ-4bit`
   * that makes layers 5 and 6 disagree while holding byte-identical tensors:
   * both fold an 8-bit `in_proj_qkv` with a 4-bit `in_proj_a`, and one reads
   * `8@64` where the other reads `4@64`, from merge order alone (owner,
   * 2026-08-19).
   *
   * The per-tensor labels survive on `cell.tensors`, so the note reads them
   * there and names what is actually in the cell.
   */
  const labels = [
    ...new Set(
      cells.flatMap((c) => c.tensors.map((t) => t.quantLabel)).filter(Boolean),
    ),
  ] as string[];
  if (labels.length > 0) {
    const entry = EXPLANATIONS["quant-scheme"];
    /*
     * The worked arithmetic only when one scheme covers the whole cell.
     *
     * With more than one, `effectiveBpw` averages parts that cost different
     * amounts, and attributing that gap to the sidecars states a falsehood in
     * proportion to how mixed the cell is: a `4@64` label beside an 8.48 average
     * printed "the extra 4.48 is the scale and offset", roughly nine times the
     * largest overhead any real scheme has. Mixed cells get their schemes named
     * and no arithmetic — the honest version is a per-scheme breakdown, which is
     * a table, and this is a hover panel.
     *
     * Compare mode with differing schemes lands here too, for the same reason.
     */
    const single = labels.length === 1 && cells.length === 1 ? cells[0] : undefined;
    const cost = single
      ? schemeCostSentence(labels[0] as string, single.effectiveBpw)
      : undefined;
    const mixed =
      labels.length > 1
        ? ` This cell folds tensors quantized differently, so the bits per weight beside it is the average across them rather than the cost of any one.${schemeBreakdown(
            cells,
          )}`
        : "";
    notes.push(
      `<div class="type-note"><strong>${escapeHtml(labels.join(", "))}</strong> ${
        entry.body
      }${cost ? ` ${cost}` : ""}${mixed}</div>`,
    );
  }

  for (const cell of cells) {
    for (const dtype of cell.dtypes) {
      if (seen.has(dtype)) continue;
      seen.add(dtype);
      const entry = explainForDtype(dtype);
      // No entry means a dtype we have not described — the header parser lets
      // unknown ones through on purpose. It stays named above and unexplained
      // here, rather than getting invented copy.
      if (!entry) continue;
      notes.push(
        `<div class="type-note"><strong>${escapeHtml(dtype)}</strong> ${entry.body}</div>`,
      );
    }
  }
  return notes.join("");
}

export function cellDetail(cell: AnatomyCell, bCell?: AnatomyCell | null): string {
  const where =
    cell.layer === undefined ? cell.role : `layer ${cell.layer} · ${cell.role}`;
  const names = tipNames(cell.tensors.map((t) => t.name));
  const warn = cell.crossCheckError
    ? `<div class="anatomy-tip-warn">${escapeHtml(cell.crossCheckError)}</div>`
    : "";

  // `null` means "compare mode, and B has no such cell"; `undefined` means
  // "not compare mode". The two produce different panels, so they cannot be
  // collapsed into one absent value.
  if (bCell === undefined) {
    return `<strong>${escapeHtml(where)}</strong> · ${cellFacts(cell)}<div class="anatomy-tip-names mono">${names}</div>${warn}`;
  }

  const bLine = bCell
    ? `<div><strong>B</strong> · ${cellFacts(bCell)}</div>`
    : "<div><strong>B</strong> · no tensors here — this cell exists in A only</div>";
  return `<strong>${escapeHtml(where)}</strong>
    <div><strong>A</strong> · ${cellFacts(cell)}</div>
    ${bLine}
    <div class="anatomy-tip-names mono">${names}</div>${warn}`;
}

/**
 * Say when a scheme could not be pinned down, rather than colouring the cell
 * with a number we had to guess.
 *
 * The packed and scale widths constrain only the *product* of bits and group
 * size, so several genuinely different schemes fit the same shapes and all
 * unpack without error. Choosing one would scale the reported bits-per-weight
 * by exactly the factor the choice was wrong by — a plausible, wrong headline
 * number, which is worse than an absent one.
 */
function renderAmbiguity(anatomy: Anatomy): string {
  /*
   * Only the *genuinely* unresolved ones. A tensor declined by a recognized
   * format is also `ambiguous`, and the sentence below — "the stored shapes fix
   * only the product of bits × group size" — is true of an MLX triple and false
   * of EXL3, whose shapes are unambiguous and whose reading is merely
   * unverified. Both notices rendered together on the EXL3 checkpoint, giving
   * two contradictory reasons for the same 400 tensors.
   */
  const unresolved = anatomy.ambiguousCount - anatomy.declinedCount;
  if (unresolved <= 0) return "";
  return `<div class="notice notice--info">
    <strong>${unresolved} quantized tensor${
      unresolved === 1 ? "" : "s"
    } could not be pinned to a scheme.</strong>
    The stored shapes fix only the product of bits × group size, so several settings fit equally
    and all of them unpack without error. This repo's config doesn't narrow it, so those cells
    report no bits-per-weight rather than a number that could be wrong by a factor of two.
  </div>`;
}

/**
 * Config-vs-shapes disagreement, surfaced rather than resolved.
 *
 * The plan requires this to fail loud. Quietly preferring one source would put
 * a confident `4@32` label on a grid that the tensor shapes contradict.
 */
function renderCrossCheck(anatomy: Anatomy): string {
  if (anatomy.crossCheckErrors.length === 0) return "";
  return `<div class="notice notice--warn">
    <strong>Quantization metadata disagrees with the tensor shapes.</strong>
    The repo's config and the actual packed dimensions imply different settings, so the
    <code>bits@group</code> labels below cannot all be right. Reported rather than resolved —
    picking one source silently would be a guess wearing a fact's clothing.
    <ul>${anatomy.crossCheckErrors
      .slice(0, 5)
      .map((e) => `<li class="mono">${escapeHtml(e)}</li>`)
      .join("")}</ul>
  </div>`;
}

/**
 * Show the working.
 *
 * Ruled at Round 1: derive component names from tensor-name structure
 * transparently, never silently. A reader who disagrees with a placement can
 * see exactly which rule put it there.
 */
/**
 * How much of the model, by weight, we could not name.
 *
 * The count alone understates and overstates by turns, and the two failures look
 * identical on screen. `DeepSeek-V4-Flash-0731` has 633 unrecognized tensors
 * that are 0.5% of it — numerous because they are tiny. Before the fused
 * feed-forward rules landed, `gpt-oss-120b` had far fewer unrecognized tensors
 * that were **93.5%** of its bytes. A reader deciding whether to trust the grid
 * needs the second number more than the first.
 */
function unnamedByteShare(anatomy: Anatomy): number {
  let total = 0;
  let unnamed = 0;
  for (const cell of anatomy.cells.values()) {
    total += cell.bytes;
    if (cell.role === "other") unnamed += cell.bytes;
  }
  return total > 0 ? unnamed / total : 0;
}

/**
 * A share, floored rather than rounded to zero.
 *
 * `0.0%` beside a non-zero tensor count reads as a contradiction, and rounding
 * a real gap down to nothing is the one direction this number must never fail
 * in.
 */
function formatShare(share: number): string {
  if (share <= 0) return "0%";
  if (share < 0.001) return "<0.1%";
  return `${(share * 100).toFixed(1)}%`;
}

/**
 * What the scheme spends on weights, and what it spends describing them.
 *
 * Asked for by the reader whose report produced the AWQ/GPTQ fix: *"not what a
 * tensor is stored at, but how much of a scheme's budget goes to the codebook
 * instead of the weights."* Every figure here was already computed; none of it
 * was on screen.
 *
 * The payload half is the load-bearing part. It comes out at the nominal width
 * — a round 4.00 on all four schemes this tool resolves — which is what makes
 * the line a check a reader can run rather than a number they have to take on
 * faith. If it ever reads 3.87, the parameter count behind it is wrong.
 *
 * Three decimals on the codebook figure, two on the rest: 0.156 and 0.169 are
 * the difference between AWQ and GPTQ, and at two decimals they would both
 * round to 0.16 and the comparison the panel exists to support would vanish.
 */
/**
 * Where the non-parameter bytes went, when a checkpoint ships any.
 *
 * ## Why this is a disclosure rather than a footnote
 *
 * Excluding buffers from `params` is the fix for report `79b5cce5`, and on its
 * own it makes 48 MiB of `openai-community/gpt2` vanish from every figure a
 * reader can see: the parameter count drops by 12.6M, and nothing on the page
 * says where those bytes went. That is a second defect wearing the first one's
 * clothes — the numbers become right and the page stops accounting for its own
 * file.
 *
 * So the identity is stated outright: file bytes = weight bytes + buffer bytes,
 * with all three printed. A reader who checks the byte total against the Hub
 * gets the number they expect, and a reader who wonders why 523 MiB of file
 * holds 124M F32 parameters gets the answer in the same place.
 *
 * Absent entirely when a model ships no buffers, which is most of them — a row
 * reading "0 B in buffers" would be noise on every modern checkpoint.
 */
function renderBuffers(anatomy: Anatomy): string {
  const totals = anatomyTotals(anatomy);
  if (totals.bufferBytes <= 0) return "";
  const tensors = [...anatomy.cells.values()]
    .filter((c) => c.isBuffer)
    .reduce((n, c) => n + c.tensors.length, 0);
  // The rules that actually fired, so the summary names what was excluded
  // rather than asserting a category. Deduped: twelve identical masks are one
  // reason, not twelve.
  const reasons = [
    ...new Set(
      [...anatomy.cells.values()]
        .filter((c) => c.isBuffer)
        .flatMap((c) => c.tensors.map((t) => t.placement.rule)),
    ),
  ];
  return `<details class="anatomy-rules">
    <summary>Non-parameter buffers — ${formatBytes(
      totals.bufferBytes,
    )} across ${tensors.toLocaleString()} tensor${
      tensors === 1 ? "" : "s"
    }, not counted as parameters</summary>
    <ul>${reasons.map((r) => `<li class="mono">${escapeHtml(r)}</li>`).join("")}</ul>
    <p class="prose prose--muted">These are stored in the file but are not learned weights — a
    causal mask, rotary frequencies, a table of position ids. PyTorch registers them as buffers
    and does not count them, so neither do we: <strong>${formatParams(
      totals.params,
    )} parameters</strong> is what the model has.</p>
    <p class="prose prose--muted">Their bytes are still yours to download, so they stay in the
    total: ${formatBytes(totals.weightBytes)} of weights + ${formatBytes(
      totals.bufferBytes,
    )} of buffers = ${formatBytes(
      totals.bytes,
    )}. Bits per weight is measured over the weights alone, which is why it stays
    ${totals.effectiveBpw.toFixed(2)} rather than rising to
    ${
      totals.params > 0 ? ((totals.bytes * 8) / totals.params).toFixed(2) : "—"
    } — buffer bytes are not a cost each weight pays.</p>
  </details>`;
}

/**
 * The EXL3 finding, shown as pending rather than asserted.
 *
 * A notice rather than a `<details>`: the reader is looking at a model whose
 * parameter count is deliberately absent from the header, and the explanation
 * for that absence must not be behind a disclosure triangle. `notice--info`
 * rather than `--warn` because nothing is wrong — the file is fine, the format
 * is documented, and the gap is ours.
 */
function renderExl3(notice: string | undefined): string {
  if (!notice) return "";
  return `<div class="notice notice--info">${escapeHtml(notice)}</div>`;
}

function renderBudget(anatomy: Anatomy): string {
  const b = anatomy.budget;
  if (!b) return "";
  return `<details class="anatomy-rules">
    <summary>Quantization budget — ${b.payloadBpw.toFixed(
      2,
    )} bits of weights + ${b.codebookBpw.toFixed(3)} of codebook = ${b.effectiveBpw.toFixed(
      2,
    )} bits/weight</summary>
    <p class="prose prose--muted">Across ${b.tensors.toLocaleString()} quantized tensor${
      b.tensors === 1 ? "" : "s"
    }, <strong>${formatShare(
      b.codebookShare,
    )}</strong> of the stored bits describe the weights rather than being weights: scale
    planes, zero points, and group indices where the scheme keeps them. The payload alone is the
    scheme's nominal width, so a figure that is not round means the parameter count behind it is
    wrong.</p>
    <p class="prose prose--muted">Overhead is a property of the scheme, not of the model — it
    tracks how much metadata each group carries and how many values share it.</p>
  </details>`;
}

/**
 * How much of the grid to believe.
 *
 * A map whose roles are 91.6% guessed is a different object from one that is
 * 0.4% guessed, and until now they read with identical weight — the same
 * muted line in the same collapsed disclosure. Reported from a dev-server
 * review of `MiniMaxAI/MiniMax-H3`'s audio VAE, whose 896 unrecognized tensors
 * are 91.6% of its bytes: a grid worth almost nothing, presented as calmly as
 * a perfect one.
 *
 * The bands are the owner's (2026-08-09). What they encode:
 *
 * - **under 5%** — the long tail every architecture has. Nothing to say.
 * - **5% to 30%** — a real gap. Some component is unnamed, and a reader
 *   comparing roles should know which conclusions rest on it.
 * - **over 30%** — the map is mostly "other". Whatever it appears to show
 *   about this model's shape is an artefact of what could not be named.
 *
 * Colour is redundant here, never the message: the percentage is already in
 * the sentence and says the same thing. That is what keeps this legible when
 * the hue is not — the failure mode of a status colour is a reader who cannot
 * tell it from the text around it, and such a reader still gets the number.
 */
export type PlacementConfidence = "clear" | "caution" | "alarm";

const CAUTION_SHARE = 0.05;
const ALARM_SHARE = 0.3;

export function placementConfidence(share: number): PlacementConfidence {
  if (share > ALARM_SHARE) return "alarm";
  if (share >= CAUTION_SHARE) return "caution";
  return "clear";
}

function renderRules(anatomy: Anatomy, formatNotice?: string): string {
  const share = unnamedByteShare(anatomy);
  const unnamedShare = formatShare(share);
  const confidence = placementConfidence(share);
  return `<details class="anatomy-rules">
    <summary>How tensors were placed${
      formatNotice
        ? ` — <span class="placement-share placement-share--caution">unpublished tensor format</span>`
        : ""
    }${
      anatomy.unclassified > 0
        ? ` — <span class="placement-share placement-share--${confidence}">${anatomy.unclassified.toLocaleString()} unrecognized (${unnamedShare} of bytes)</span>, shown as “other”`
        : ""
    }</summary>
    <ul>${anatomy.rulesUsed
      .map((r) => `<li class="mono">${escapeHtml(r)}</li>`)
      .join("")}</ul>
    <p class="prose prose--muted">Placement is by name pattern, so an unfamiliar architecture may
    land tensors in “other”. That is a gap in the rule table, not a claim about the model.</p>
    ${
      formatNotice
        ? `<p class="prose prose--muted">${escapeHtml(formatNotice)}. Sizes come from the
      file&rsquo;s own layout, so the byte figures are exact; the width is measured rather than
      read from a specification.</p>`
        : ""
    }
  </details>`;
}
